// Vendor search service
// - Service-based categories (roofing, HVAC, plumbing, etc.) → Google Places API
// - Product-based categories (laptops, cars, appliances, etc.) → Perplexity via AWS Lambda

import { getChatCompletion } from './aiIntegrations/chatCompletion';

// ─────────────────────────────────────────────────────────────────────────────
// PASTE YOUR GOOGLE PLACES API KEY HERE
// ─────────────────────────────────────────────────────────────────────────────
const GOOGLE_PLACES_API_KEY = import.meta.env?.VITE_GOOGLE_PLACES_API_KEY || 'your-google-places-api-key-here';
// ─────────────────────────────────────────────────────────────────────────────

// Categories that use Google Places (local service businesses)
const SERVICE_CATEGORIES = new Set([
  // Home & Property — services
  'roofing',
  'hvac',
  'plumbing',
  'electrical',
  'painting',
  'flooring',
  'windows-doors',
  'fencing',
  'landscaping',
  'pest-control',
  'pool-installation',
  'solar-panels',
  // Vehicle & Auto — services
  'auto-repair',
  'detailing',
  'body-shop',
  // Health & Wellness — services
  'dental-care',
  'vision-care',
  'mental-health',
  'physical-therapy',
  'personal-training',
  'gym-fitness',
  'chiropractic',
  'nutrition-dietitian',
  'weight-loss-programs',
  'cosmetic-procedures',
  // Life Events — services
  'childcare',
  'weddings',
  'funerals',
  'private-school',
  // Finance / Professional — services
  'accountants',
  'attorneys',
  'financial-advisors',
  'moving-companies',
  'tutors',
  // Education — services
  'tutoring',
  // Insurance — services
  'car-insurance',
  'auto-insurance',
  'homeowners-insurance',
  'renters-insurance',
  'life-insurance',
  'health-insurance',
  'pet-insurance',
  'business-insurance',
  // Travel & Experiences — services
  'events',
  'gym-memberships',
]);

// Human-readable search terms for Google Places queries
const categorySearchTerms = {
  // ── SERVICE categories → Google Places ──────────────────────────────────
  // Home & Property
  'roofing': 'roofing contractors',
  'hvac': 'HVAC heating and cooling companies',
  'plumbing': 'plumbers',
  'electrical': 'electricians',
  'painting': 'painting contractors',
  'flooring': 'flooring installation companies',
  'windows-doors': 'window and door installation companies',
  'fencing': 'fencing contractors',
  'landscaping': 'landscaping companies',
  'pest-control': 'pest control companies',
  'pool-installation': 'pool installation companies',
  'solar-panels': 'solar panel installation companies',
  // Vehicle & Auto
  'auto-repair': 'auto repair shops',
  'detailing': 'auto detailing shops',
  'body-shop': 'auto body shops',
  // Health & Wellness
  'dental-care': 'dental offices',
  'vision-care': 'optometrists and vision centers',
  'mental-health': 'mental health therapists and counselors',
  'physical-therapy': 'physical therapy clinics',
  'personal-training': 'personal trainers',
  'gym-fitness': 'gyms and fitness centers',
  'chiropractic': 'chiropractic clinics',
  'nutrition-dietitian': 'registered dietitians and nutrition counselors',
  'weight-loss-programs': 'weight loss clinics and programs',
  'cosmetic-procedures': 'cosmetic surgery and med spa clinics',
  // Life Events
  'childcare': 'childcare centers and daycares',
  'weddings': 'wedding planners and venues',
  'funerals': 'funeral homes',
  'private-school': 'private schools',
  // Finance / Professional
  'accountants': 'accounting firms and CPAs',
  'attorneys': 'law firms and attorneys',
  'financial-advisors': 'financial advisors',
  'moving-companies': 'moving companies',
  'tutors': 'tutoring services',
  // Education
  'tutoring': 'tutoring services',
  // Insurance
  'car-insurance': 'auto insurance agencies',
  'auto-insurance': 'auto insurance agencies',
  'homeowners-insurance': 'homeowners insurance agencies',
  'renters-insurance': 'renters insurance agencies',
  'life-insurance': 'life insurance agencies',
  'health-insurance': 'health insurance agencies',
  'pet-insurance': 'pet insurance agencies',
  'business-insurance': 'business insurance agencies',
  // Travel & Experiences
  'events': 'event venues and planners',
  'gym-memberships': 'gyms and fitness centers',
  // ── PRODUCT categories → Perplexity ─────────────────────────────────────
  // Tech & Electronics
  'computers-laptops': 'computers and laptops',
  'tablets': 'tablets',
  'smartphones': 'smartphones',
  'cameras': 'cameras',
  'tv-home-theater': 'TVs and home theater systems',
  'audio-equipment': 'audio equipment and speakers',
  'smart-home-devices': 'smart home devices',
  'wearables': 'wearables and smartwatches',
  'headphones': 'headphones',
  // Home Goods
  'major-appliances': 'major home appliances',
  'furniture': 'furniture',
  'mattresses': 'mattresses',
  'home-gym': 'home gym equipment',
  'home-security': 'home security systems',
  // Vehicle & Auto — products
  'car-purchase': 'cars for purchase',
  'motorcycle': 'motorcycles',
  'boat': 'boats',
  'rv': 'RVs and recreational vehicles',
  'tires': 'tires',
  // Home & Property — products
  'generators': 'generators',
  // Finance — products
  'home-purchase': 'home purchase mortgage lenders',
  'home-refinance': 'home refinance lenders',
  'credit-cards': 'credit cards',
  'student-loans': 'student loan lenders',
  // Life Events — products
  'engagement-rings': 'engagement rings and jewelry',
  'baby-products': 'baby products',
  'stroller': 'strollers',
  'crib': 'cribs',
  'car-seat': 'car seats',
  'bassinet': 'bassinets',
  // Travel & Experiences — products
  'flights': 'flights',
  'hotels': 'hotels',
  'vacation-packages': 'vacation packages',
  'cruises': 'cruises',
  'rental-cars': 'rental cars',
  'travel-insurance': 'travel insurance',
  'concerts': 'concerts and tickets',
  'sporting-events': 'sporting event tickets',
  // Education — products
  'college': 'colleges and universities',
  'bootcamps': 'coding and trade bootcamps',
  'certifications': 'certification programs',
  'online-learning': 'online learning platforms',
  'professional-training': 'professional training programs',
  'courses': 'online courses and certifications',
  // Health & Wellness — products
  'medical-equipment': 'medical equipment',
  // Fallback
  'default': 'local service providers',
};

/**
 * Detect whether a category is service-based (local) or product-based (online)
 */
function isServiceCategory(category) {
  if (!category) return false;
  const normalized = category?.toLowerCase()?.trim();
  // Treat all "other-*" subcategories as service-based (local search)
  if (normalized?.startsWith('other-')) return true;
  return SERVICE_CATEGORIES?.has(normalized);
}

/**
 * Search for vendors — routes to Google Places for service categories,
 * Perplexity for product categories.
 *
 * @param {string} category       - The project category key
 * @param {string} zipCode        - Zip code for geographic search
 * @param {string} city           - Optional city name
 * @param {object} projectDetails - Optional project details for fallback queries
 *   { projectName, description, subcategory }
 * @returns {Promise<Array>} Array of vendor objects
 */
export async function searchGoogleVendors(category, zipCode, city = '', projectDetails = {}) {
  const searchTerm = categorySearchTerms?.[category] || categorySearchTerms?.['default'];
  const location = city ? `${city} (${zipCode})` : `zip code ${zipCode}`;

  if (isServiceCategory(category)) {
    // Service-based → Google Places API
    const hasKey = GOOGLE_PLACES_API_KEY && GOOGLE_PLACES_API_KEY !== 'your-google-places-api-key-here';
    if (!hasKey) {
      console.warn('Google Places API key not set. Falling back to Perplexity for service category:', category);
      return await searchViaPerplexity(searchTerm, location, zipCode, category, projectDetails);
    }
    try {
      return await searchViaGooglePlaces(searchTerm, zipCode, category, projectDetails);
    } catch (err) {
      console.warn('Google Places API failed, falling back to Perplexity:', err);
      return await searchViaPerplexity(searchTerm, location, zipCode, category, projectDetails);
    }
  } else {
    // Product-based → Perplexity via AWS Lambda
    return await searchViaPerplexity(searchTerm, location, zipCode, category, projectDetails);
  }
}

/**
 * Build a fallback search query from project details when the primary query returns no results.
 */
function buildFallbackQuery(zipCode, projectDetails = {}) {
  const { projectName, description, subcategory } = projectDetails;
  const parts = [];

  if (projectName && projectName?.trim()) {
    parts?.push(projectName?.trim());
  }
  if (subcategory && subcategory?.trim() && subcategory?.toLowerCase() !== 'other') {
    parts?.push(subcategory?.trim());
  }
  if (description && description?.trim()) {
    // Use first ~60 chars of description as a hint
    const hint = description?.trim()?.slice(0, 60)?.replace(/\s+\S*$/, '');
    if (hint) parts?.push(hint);
  }

  if (parts?.length === 0) {
    return `local service providers near ${zipCode}`;
  }

  return `${parts?.join(' ')} near ${zipCode}`;
}

/**
 * Search using Google Places Text Search API
 */
async function searchViaGooglePlaces(searchTerm, zipCode, category, projectDetails = {}) {
  const isOtherCategory = category?.toLowerCase()?.startsWith('other-');
  const primaryQuery = isOtherCategory && projectDetails?.projectName?.trim()
    ? `${projectDetails?.projectName?.trim()} near ${zipCode}`
    : `${searchTerm} near ${zipCode}`;

  // Use the NEW Places API v1 (places.googleapis.com) — supports browser CORS.
  const url = 'https://places.googleapis.com/v1/places:searchText';

  const headers = {
    'Content-Type': 'application/json',
    'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
    'X-Goog-FieldMask': 'places.id,places.displayName,places.formattedAddress,places.nationalPhoneNumber,places.internationalPhoneNumber,places.websiteUri,places.rating,places.userRatingCount,places.shortFormattedAddress,places.businessStatus,places.priceLevel,places.reviews,places.regularOpeningHours',
  };

  const doSearch = async (query) => {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ textQuery: query, maxResultCount: 5 }),
    });

    if (!response?.ok) {
      const errText = await response?.text();
      throw new Error(`Google Places API request failed (${response?.status}): ${errText}`);
    }

    const data = await response?.json();
    return data?.places || [];
  };

  // Primary search
  let places = await doSearch(primaryQuery);

  // Fallback: retry with project-specific query if no results
  if (places?.length === 0) {
    const fallbackQuery = buildFallbackQuery(zipCode, projectDetails);
    console.info(`Google Places: no results for "${primaryQuery}", retrying with fallback: "${fallbackQuery}"`);
    places = await doSearch(fallbackQuery);
  }

  if (places?.length === 0) {
    throw new Error('Google Places: no results returned');
  }

  return places?.slice(0, 5)?.map(place => {
    // Extract top review snippet if available
    const topReview = place?.reviews?.[0];
    const reviewSnippet = topReview?.text?.text || topReview?.originalText?.text || '';
    const reviewAuthor = topReview?.authorAttribution?.displayName || '';
    const reviewRating = topReview?.rating || null;

    return {
      id: `google-${place?.id}`,
      name: place?.displayName?.text || place?.displayName || '',
      address: place?.formattedAddress || place?.shortFormattedAddress || '',
      rating: place?.rating || null,
      totalReviews: place?.userRatingCount || 0,
      phone: place?.nationalPhoneNumber || place?.internationalPhoneNumber || '',
      website: place?.websiteUri || '',
      email: '',
      notes: place?.shortFormattedAddress || '',
      isRegistered: false,
      isGoogleResult: true,
      googlePlaceId: place?.id,
      googleMapsUrl: place?.id ? `https://www.google.com/maps/place/?q=place_id:${place?.id}` : null,
      businessStatus: place?.businessStatus,
      priceLevel: place?.priceLevel,
      reviewSnippet,
      reviewAuthor,
      reviewRating,
      reviewCount: place?.reviews?.length || 0,
      isOpen: place?.regularOpeningHours?.openNow ?? null,
      category,
      source: 'google_places',
    };
  });
}

/**
 * Search using Perplexity via AWS Lambda (for product-based categories)
 */
async function searchViaPerplexity(searchTerm, location, zipCode, category, projectDetails = {}) {
  const { projectName, description } = projectDetails;

  // Build a context-enriched search term when project details are available
  let enrichedSearchTerm = searchTerm;
  if (projectName && projectName?.trim()) {
    enrichedSearchTerm = `${projectName?.trim()} (${searchTerm})`;
  } else if (description && description?.trim()) {
    const hint = description?.trim()?.slice(0, 80)?.replace(/\s+\S*$/, '');
    if (hint) enrichedSearchTerm = `${searchTerm} - ${hint}`;
  }

  const prompt = `List 5 ${enrichedSearchTerm} businesses near ${zipCode}.

Respond with ONLY a JSON array. No text before or after. No markdown. No code blocks.

Example format:
[{"name":"ABC Company","phone":"555-555-5555","website":"https://example.com","address":"123 Main St, City, ST 12345","googleRating":4.5,"reviewCount":120,"description":"Brief description"},{"name":"XYZ Services","phone":"555-123-4567","website":"https://xyz.com","address":"456 Oak Ave, City, ST 12345","googleRating":4.2,"reviewCount":85,"description":"Another description"}]

Start your response with [ and end with ]`;

  let content = '';
  try {
    const response = await getChatCompletion(
      'PERPLEXITY',
      'llama-3.1-sonar-small-128k-online',
      [{ role: 'user', content: prompt }],
      { temperature: 0.1, max_tokens: 1500 }
    );
    content = response?.choices?.[0]?.message?.content || '';
  } catch (apiErr) {
    console.error('Perplexity API call failed:', apiErr);
    return [];
  }

  if (!content) {
    console.warn('Perplexity returned empty content for:', enrichedSearchTerm);
    return [];
  }

  // Attempt to extract and parse JSON from the response
  const vendors = extractVendorJson(content);
  if (!vendors || vendors?.length === 0) {
    console.warn('Could not parse vendor data from Perplexity response. Raw content:', content?.slice(0, 300));
    return [];
  }

  return vendors?.slice(0, 5)?.map((v, idx) => ({
    id: `search-${Date.now()}-${idx}`,
    name: v?.name || 'Unknown Business',
    phone: v?.phone || '',
    website: v?.website || '',
    email: '',
    address: v?.address || '',
    googleRating: typeof v?.googleRating === 'number' ? v?.googleRating : parseFloat(v?.googleRating) || null,
    reviewCount: parseInt(v?.reviewCount) || 0,
    notes: v?.description || '',
    isRegistered: false,
    isGoogleResult: true,
    googleMapsUrl: v?.name ? `https://www.google.com/maps/search/${encodeURIComponent(v?.name + ' ' + (v?.address || zipCode))}` : null,
    category,
    source: 'perplexity',
  }));
}

/**
 * Robustly extract a JSON array of vendor objects from an LLM response string.
 * Handles: clean JSON, markdown fences, leading/trailing text, single objects.
 */
function extractVendorJson(content) {
  if (!content) return null;

  // 1. Strip markdown code fences and common LLM preamble
  let cleaned = content
    ?.replace(/```(?:json)?\s*/gi, '')
    ?.replace(/```\s*/g, '')
    ?.replace(/^[^\[{]*/s, (match) => {
      // Only strip leading text if there's a [ or { coming
      return content?.includes('[') || content?.includes('{') ? match?.replace(/^[^\[{]+/, '') : match;
    })
    ?.trim();

  // Re-trim to start at first [ or {
  const firstBracket = cleaned?.search(/[\[{]/);
  if (firstBracket > 0) cleaned = cleaned?.slice(firstBracket);

  // 2. Try direct parse first
  try {
    const parsed = JSON.parse(cleaned);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return [parsed];
  } catch (_) {}

  // 3. Extract the first [...] block (non-greedy)
  const arrayMatch = cleaned?.match(/\[\s*\{[\s\S]*?\}\s*\]/);
  if (arrayMatch) {
    try {
      const parsed = JSON.parse(arrayMatch?.[0]);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
  }

  // 4. Try a greedy array match (handles nested objects)
  const greedyMatch = cleaned?.match(/\[[\s\S]*\]/);
  if (greedyMatch) {
    try {
      const parsed = JSON.parse(greedyMatch?.[0]);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
  }

  // 5. Try fixing common JSON issues (trailing commas, single quotes)
  if (greedyMatch) {
    try {
      const fixed = greedyMatch?.[0]
        ?.replace(/,\s*([}\]])/g, '$1')   // remove trailing commas
        ?.replace(/'/g, '"')              // replace single quotes
        ?.replace(/([{,]\s*)(\w+)(\s*:)/g, '$1"$2"$3'); // quote unquoted keys
      const parsed = JSON.parse(fixed);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return [parsed];
    } catch (_) {}
  }

  // 6. Extract individual {...} objects and collect them into an array
  const objectMatches = cleaned?.match(/\{[^{}]*\}/g);
  if (objectMatches && objectMatches?.length > 0) {
    const objects = [];
    for (const objStr of objectMatches) {
      try {
        const obj = JSON.parse(objStr);
        if (obj && typeof obj === 'object' && obj?.name) objects?.push(obj);
      } catch (_) {
        // Try fixing this individual object too
        try {
          const fixed = objStr
            ?.replace(/,\s*}/g, '}')
            ?.replace(/'/g, '"')
            ?.replace(/([{,]\s*)(\w+)(\s*:)/g, '$1"$2"$3');
          const obj = JSON.parse(fixed);
          if (obj && typeof obj === 'object' && obj?.name) objects?.push(obj);
        } catch (_2) {}
      }
    }
    if (objects?.length > 0) return objects;
  }

  // 7. Last resort: try to extract name/phone/address via regex from plain text
  const nameMatches = content?.match(/(?:name|business)[":\s]+([A-Z][\w\s&'-]{2,50})/gi);
  if (nameMatches && nameMatches?.length > 0) {
    return nameMatches?.slice(0, 5)?.map((m, i) => ({
      name: m?.replace(/(?:name|business)[":\s]+/i, '')?.trim()?.replace(/["',]/g, ''),
      phone: '',
      website: '',
      address: '',
      description: 'Found via online search',
    }));
  }

  return null;
}
